import { Button } from "@/components/ui/button";
import {
    Card,
    CardHeader
} from "@/components/ui/card";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api } from "@/utils/api";
import { ChevronLeft, ChevronRight, ImageIcon, Loader2, Plus, Search, Trash2, Upload } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";

export default function CoverImageManagement() {
    const [images, setImages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showUploadDialog, setShowUploadDialog] = useState(false);
    const [uploading, setUploading] = useState(false);

    const [name, setName] = useState("");
    const [selectedFile, setSelectedFile] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [dragActive, setDragActive] = useState(false);

    const [uploadMode, setUploadMode] = useState("file"); // 'file' or 'link'
    const [externalUrl, setExternalUrl] = useState("");

    // Pagination & Search States
    const [searchQuery, setSearchQuery] = useState("");
    const [searchVal, setSearchVal] = useState(""); // input value
    const [currentPage, setCurrentPage] = useState(1);
    const [totalItems, setTotalItems] = useState(0);
    const [hasMore, setHasMore] = useState(false);
    const pageSize = 8;

    useEffect(() => {
        loadCoverImages();
    }, [currentPage, searchQuery]);

    const loadCoverImages = async () => {
        try {
            setLoading(true);
            const response = await api.getCoverImages({
                page: currentPage,
                limit: pageSize,
                search: searchQuery || undefined
            });
            setImages(response.data.items || []);
            setTotalItems(response.data.total || 0);
            setHasMore(response.data.has_more || false);
        } catch (err) {
            console.error("Failed to load cover images:", err);
            toast.error("Failed to load cover images from S3.");
        } finally {
            setLoading(false);
        }
    };

    const handleSearchSubmit = (e) => {
        e.preventDefault();
        setCurrentPage(1);
        setSearchQuery(searchVal);
    };

    const handleClearSearch = () => {
        setSearchVal("");
        setSearchQuery("");
        setCurrentPage(1);
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (!file.type.startsWith("image/")) {
                toast.error("Please select an image file (png, jpg, jpeg, webp).");
                return;
            }
            setSelectedFile(file);
            setPreviewUrl(URL.createObjectURL(file));
            if (!name) {
                // Pre-populate name field with clean filename
                const baseName = file.name.split(".").slice(0, -1).join(".");
                setName(baseName.replace(/[-_]/g, " "));
            }
        }
    };

    const handleDrag = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === "dragenter" || e.type === "dragover") {
            setDragActive(true);
        } else if (e.type === "dragleave") {
            setDragActive(false);
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);

        const file = e.dataTransfer.files[0];
        if (file) {
            if (!file.type.startsWith("image/")) {
                toast.error("Please select an image file (png, jpg, jpeg, webp).");
                return;
            }
            setSelectedFile(file);
            setPreviewUrl(URL.createObjectURL(file));
            if (!name) {
                const baseName = file.name.split(".").slice(0, -1).join(".");
                setName(baseName.replace(/[-_]/g, " "));
            }
        }
    };

    const handleUploadSubmit = async (e) => {
        e.preventDefault();
        if (!name.trim()) {
            toast.error("Please enter a name for the cover image.");
            return;
        }
        if (uploadMode === "file" && !selectedFile) {
            toast.error("Please choose an image file to upload.");
            return;
        }
        if (uploadMode === "link" && !externalUrl.trim()) {
            toast.error("Please enter a valid image URL.");
            return;
        }

        try {
            setUploading(true);
            if (uploadMode === "file") {
                await api.createCoverImage(name, selectedFile, null);
            } else {
                await api.createCoverImage(name, null, externalUrl);
            }
            toast.success("Cover image uploaded successfully to S3!");
            resetForm();
            loadCoverImages();
        } catch (err) {
            console.error("Upload error:", err);
            toast.error(err.response?.data?.detail || "Could not upload image to S3.");
        } finally {
            setUploading(false);
        }
    };

    const handleDelete = async (imageId) => {
        if (!window.confirm("Are you sure you want to delete this cover image? This action will remove it from AWS S3 permanently.")) {
            return;
        }

        try {
            await api.deleteCoverImage(imageId);
            toast.success("Cover image removed successfully.");
            loadCoverImages();
        } catch (err) {
            console.error("Delete error:", err);
            toast.error("Could not delete cover image.");
        }
    };

    const resetForm = () => {
        setName("");
        setSelectedFile(null);
        setPreviewUrl(null);
        setExternalUrl("");
        setUploadMode("file");
        setShowUploadDialog(false);
    };

    return (
        <div className="p-6 max-w-7xl mx-auto space-y-6">
            {/* Header section with glassmorphism style or premium gradients */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-white p-6 rounded-2xl border border-gray-100 shadow-sm">
                <div>
                    <h1 className="text-3xl font-extrabold text-gray-900 tracking-tight">
                        Cover Images
                    </h1>
                    <p className="text-gray-500 mt-1 text-sm sm:text-base">
                        Manage catalog cover images integrated with AWS S3 storage.
                    </p>
                </div>
                <Button
                    onClick={() => setShowUploadDialog(true)}
                    className="bg-orange-600 hover:bg-orange-700 text-white rounded-xl px-5 py-2.5 flex items-center gap-2 shadow-md shadow-orange-100 transition-all active:scale-[0.98]"
                >
                    <Plus className="w-5 h-5" />
                    Upload Image
                </Button>
            </div>

            {/* Search and Filters Bar */}
            <div className="flex flex-col sm:flex-row gap-4 justify-between items-center bg-white p-4 rounded-2xl border border-gray-100 shadow-sm">
                <form onSubmit={handleSearchSubmit} className="relative w-full sm:max-w-md flex items-center gap-2">
                    <div className="relative flex-1">
                        <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                        <Input
                            placeholder="Search cover images by name..."
                            value={searchVal}
                            onChange={(e) => setSearchVal(e.target.value)}
                            className="pl-9 pr-8 rounded-xl border-gray-200 focus-visible:ring-orange-500 focus-visible:border-orange-500"
                        />
                        {searchVal && (
                            <button
                                type="button"
                                onClick={handleClearSearch}
                                className="absolute right-3 top-2.5 text-xs text-gray-400 hover:text-gray-600 font-semibold"
                            >
                                Clear
                            </button>
                        )}
                    </div>
                    <Button type="submit" className="bg-gray-800 hover:bg-gray-900 text-white rounded-xl px-4 py-2">
                        Search
                    </Button>
                </form>

                <div className="text-sm text-gray-500 font-medium">
                    Total: {totalItems} images
                </div>
            </div>

            {/* Content area */}
            {loading ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                    {[1, 2, 3, 4].map((i) => (
                        <Card key={i} className="animate-pulse border border-gray-100 rounded-2xl overflow-hidden shadow-sm">
                            <div className="bg-gray-200 h-48 w-full" />
                            <CardHeader className="p-4 space-y-2">
                                <div className="h-4 bg-gray-200 rounded w-2/3" />
                                <div className="h-3 bg-gray-200 rounded w-1/2" />
                            </CardHeader>
                        </Card>
                    ))}
                </div>
            ) : images.length === 0 ? (
                <div className="flex flex-col items-center justify-center p-16 bg-white border border-dashed border-gray-200 rounded-3xl text-center shadow-sm">
                    <div className="p-4 bg-orange-50 text-orange-600 rounded-full mb-4">
                        <ImageIcon className="w-10 h-10" />
                    </div>
                    <h3 className="text-lg font-bold text-gray-800">
                        {searchQuery ? "No search results" : "No cover images found"}
                    </h3>
                    <p className="text-gray-500 mt-1 max-w-sm text-sm">
                        {searchQuery
                            ? `We couldn't find any images matching "${searchQuery}". Try a different keyword.`
                            : "Upload some beautiful high-resolution cover images to get started. They will be uploaded directly to AWS S3."}
                    </p>
                    <div className="flex gap-3 mt-5">
                        {searchQuery && (
                            <Button
                                onClick={handleClearSearch}
                                variant="outline"
                                className="rounded-xl"
                            >
                                Clear Search
                            </Button>
                        )}
                        <Button
                            onClick={() => setShowUploadDialog(true)}
                            className="bg-orange-600 hover:bg-orange-700 text-white rounded-xl"
                        >
                            Upload Cover Image
                        </Button>
                    </div>
                </div>
            ) : (
                <div className="space-y-6">
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                        {images.map((item) => (
                            <div
                                key={item.id}
                                className="group relative bg-white border border-gray-100 rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
                            >
                                {/* Image Container with Hover Overlay */}
                                <div className="relative h-48 w-full bg-gray-50 overflow-hidden">
                                    <img
                                        src={item.image_url}
                                        alt={item.name}
                                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                                        onError={(e) => {
                                            e.target.src = "https://images.unsplash.com/photo-1507525428034-b723cf961d3e?auto=format&fit=crop&w=600&q=80"; // fallback
                                        }}
                                    />
                                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-300 flex items-center justify-center">
                                        <Button
                                            onClick={() => handleDelete(item.id)}
                                            variant="destructive"
                                            size="icon"
                                            className="opacity-0 group-hover:opacity-100 transform translate-y-2 group-hover:translate-y-0 transition-all duration-300 rounded-full h-10 w-10 bg-red-600 hover:bg-red-700 shadow-md"
                                        >
                                            <Trash2 className="w-5 h-5 text-white" />
                                        </Button>
                                    </div>
                                </div>

                                {/* Info */}
                                <div className="p-4 flex items-start justify-between gap-2">
                                    <div className="min-w-0">
                                        <h4 className="font-semibold text-gray-800 text-sm truncate group-hover:text-orange-600 transition-colors">
                                            {item.name}
                                        </h4>
                                        <p className="text-xs text-gray-400 mt-1 truncate">
                                            Added: {new Date(item.created_at).toLocaleDateString()}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Pagination Controls */}
                    {totalItems > 0 && (
                        <div className="flex items-center justify-between bg-white px-6 py-4 rounded-2xl border border-gray-100 shadow-sm">
                            <div className="text-sm text-gray-500">
                                Showing page <span className="font-semibold text-gray-800">{currentPage}</span> of{" "}
                                <span className="font-semibold text-gray-800">{Math.ceil(totalItems / pageSize)}</span> (
                                <span className="font-semibold text-gray-800">{totalItems}</span> items)
                            </div>
                            <div className="flex items-center gap-2">
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                                    disabled={currentPage === 1}
                                    className="rounded-xl border-gray-200"
                                >
                                    <ChevronLeft className="h-4 w-4 mr-1" />
                                    Previous
                                </Button>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setCurrentPage((p) => p + 1)}
                                    disabled={!hasMore}
                                    className="rounded-xl border-gray-200"
                                >
                                    Next
                                    <ChevronRight className="h-4 w-4 ml-1" />
                                </Button>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Modern Dialog Modal for Uploading */}
            <Dialog open={showUploadDialog} onOpenChange={(open) => !open && resetForm()}>
                <DialogContent className="sm:max-w-[460px] rounded-3xl p-6 bg-white shadow-2xl border-0">
                    <DialogHeader>
                        <DialogTitle className="text-xl font-extrabold text-gray-900 flex items-center gap-2">
                            <Upload className="w-5 h-5 text-orange-600" />
                            Upload Cover Image
                        </DialogTitle>
                        <DialogDescription className="text-gray-500 text-sm mt-1">
                            Choose or drag an image. File will be stored securely on AWS S3.
                        </DialogDescription>
                    </DialogHeader>

                    <form onSubmit={handleUploadSubmit} className="space-y-5 py-2">
                        {/* Selector Tabs */}
                        <div className="flex border-b border-gray-100 mb-2">
                            <button
                                type="button"
                                className={`flex-1 pb-2 text-sm font-semibold text-center border-b-2 transition-all ${uploadMode === "file"
                                    ? "border-orange-600 text-orange-600"
                                    : "border-transparent text-gray-400 hover:text-gray-600"
                                    }`}
                                onClick={() => setUploadMode("file")}
                            >
                                Upload Local File
                            </button>
                            <button
                                type="button"
                                className={`flex-1 pb-2 text-sm font-semibold text-center border-b-2 transition-all ${uploadMode === "link"
                                    ? "border-orange-600 text-orange-600"
                                    : "border-transparent text-gray-400 hover:text-gray-600"
                                    }`}
                                onClick={() => setUploadMode("link")}
                            >
                                External Link
                            </button>
                        </div>

                        {uploadMode === "file" ? (
                            /* Drag and Drop Zone */
                            <div
                                onDragEnter={handleDrag}
                                onDragOver={handleDrag}
                                onDragLeave={handleDrag}
                                onDrop={handleDrop}
                                className={`relative border-2 border-dashed rounded-2xl p-6 flex flex-col items-center justify-center cursor-pointer transition-all ${dragActive
                                    ? "border-orange-500 bg-orange-50/50"
                                    : previewUrl
                                        ? "border-emerald-200 bg-emerald-50/20"
                                        : "border-gray-200 hover:border-orange-400 hover:bg-gray-50/50"
                                    }`}
                            >
                                {previewUrl ? (
                                    <div className="relative w-full h-36 rounded-lg overflow-hidden flex items-center justify-center bg-gray-50">
                                        <img
                                            src={previewUrl}
                                            alt="Preview"
                                            className="h-full w-full object-contain"
                                        />
                                        <button
                                            type="button"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setSelectedFile(null);
                                                setPreviewUrl(null);
                                            }}
                                            className="absolute top-2 right-2 bg-red-600 hover:bg-red-700 text-white rounded-full p-1.5 shadow-md hover:scale-105 transition-all"
                                        >
                                            <Trash2 className="w-3.5 h-3.5" />
                                        </button>
                                    </div>
                                ) : (
                                    <label className="w-full flex flex-col items-center justify-center cursor-pointer">
                                        <div className="p-3 bg-orange-50 rounded-full text-orange-600 mb-2">
                                            <Upload className="w-6 h-6" />
                                        </div>
                                        <span className="text-sm font-semibold text-gray-700">
                                            Drag & drop image here
                                        </span>
                                        <span className="text-xs text-gray-400 mt-1">
                                            or click to select file
                                        </span>
                                        <input
                                            type="file"
                                            accept="image/*"
                                            onChange={handleFileChange}
                                            className="hidden"
                                        />
                                    </label>
                                )}
                            </div>
                        ) : (
                            /* External URL Input */
                            <div className="space-y-2">
                                <Label htmlFor="external-url" className="text-sm font-bold text-gray-700">
                                    Outside Link / Image URL
                                </Label>
                                <Input
                                    id="external-url"
                                    type="url"
                                    placeholder="https://example.com/image.jpg"
                                    value={externalUrl}
                                    onChange={(e) => setExternalUrl(e.target.value)}
                                    className="rounded-xl border-gray-200 focus-visible:ring-orange-500 focus-visible:border-orange-500 focus:border-orange-500 focus:ring-orange-500"
                                    required
                                />
                            </div>
                        )}

                        {/* Name Field */}
                        <div className="space-y-2">
                            <Label htmlFor="image-name" className="text-sm font-bold text-gray-700">
                                Display Name
                            </Label>
                            <Input
                                id="image-name"
                                type="text"
                                placeholder="Enter image name e.g. Europe Tour Cover"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                className="rounded-xl border-gray-200 focus-visible:ring-orange-500 focus-visible:border-orange-500 focus:border-orange-500 focus:ring-orange-500"
                                required
                            />
                        </div>

                        {/* Actions */}
                        <DialogFooter className="pt-2 sm:justify-end gap-2 flex-row">
                            <Button
                                type="button"
                                variant="outline"
                                onClick={resetForm}
                                className="rounded-xl border-gray-200 text-gray-600 hover:bg-gray-50 flex-1 sm:flex-none"
                                disabled={uploading}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                className="bg-orange-600 hover:bg-orange-700 text-white rounded-xl shadow-md flex-1 sm:flex-none"
                                disabled={uploading}
                            >
                                {uploading ? (
                                    <>
                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                        Uploading...
                                    </>
                                ) : (
                                    "Start Upload"
                                )}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    );
}
